/**
 * Site files + site updates (Site Access) — the OWNER side.
 *
 * Web parity: `components/projects/site-files-client.tsx` driving
 * `app/api/projects/[projectId]/site-files*`,
 * `app/api/projects/[projectId]/site-updates` and
 * `app/api/projects/[projectId]/document-review-assignments`. The contractor
 * (portal) consumption of the same rows keeps its own DTOs in `./portal`.
 *
 * DELIBERATELY NO `fileUrl` / `fileKey` ON THE WIRE. Those are storage
 * references the read side presigns on demand, so shipping them to a native
 * client hands out a durable pointer that outlives the caller's entitlement
 * (and the origin URL is bearer-shaped once signed). The client downloads
 * through `GET …/site-files/:fileId/download`, which re-runs the tenancy-scoped
 * loader before minting a short-lived signed URL.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";

/**
 * MUST equal the Prisma `SiteFileCategory` enum, in the same order — the web
 * routes build their request enums from this list, so a drift would change
 * their validation messages as well as the wire contract. Pinned by
 * `__tests__/api/mobile/v1/site-files/site-files-contract.test.ts`.
 */
export const SITE_FILE_CATEGORIES = [
  "DRAWING",
  "PLAN",
  "H_AND_S_DOCUMENT",
  "PROCEDURE",
  "RAMS",
  "CPP",
  "EMERGENCY_INFO",
  "OTHER",
] as const;
export type SiteFileCategory = (typeof SITE_FILE_CATEGORIES)[number];
export const SiteFileCategorySchema = z.enum(SITE_FILE_CATEGORIES);

/** MUST equal the Prisma `SiteFileStatus` enum. */
export const SITE_FILE_STATUSES = ["ACTIVE", "SUPERSEDED", "ARCHIVED"] as const;
export type SiteFileStatus = (typeof SITE_FILE_STATUSES)[number];
export const SiteFileStatusSchema = z.enum(SITE_FILE_STATUSES);

export const SiteFileSchema = z
  .object({
    id: z.string(),
    projectId: z.string(),
    title: z.string(),
    category: SiteFileCategorySchema,
    status: SiteFileStatusSchema,
    fileName: z.string().nullable(),
    contentType: z.string().nullable(),
    notes: z.string().nullable(),
    visibleInPortal: z.boolean(),
    requiresAcknowledgement: z.boolean(),
    supersededByFileId: z.string().nullable(),
    supersededAt: z.string().datetime().nullable(),
    uploadedByUserId: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type SiteFile = z.infer<typeof SiteFileSchema>;

export const SiteFileListResponse = z
  .object({ files: z.array(SiteFileSchema) })
  .strict();
export type SiteFileListResponse = z.infer<typeof SiteFileListResponse>;

export const SiteFileResponse = z.object({ file: SiteFileSchema }).strict();
export type SiteFileResponse = z.infer<typeof SiteFileResponse>;

/**
 * Create from an object already committed through
 * `POST /api/mobile/v1/uploads/{presign,commit}` with `purpose: "site_file"`.
 * `fileKey` + `fileUrl` are the commit response's own fields; the route
 * re-asserts they sit under the CALLER's storage prefix before persisting.
 */
export const SiteFileCreateRequest = z
  .object({
    title: z.string().trim().min(1).max(200),
    category: SiteFileCategorySchema.default("OTHER"),
    fileKey: z.string().min(1).max(1024),
    fileUrl: z.string().min(1).max(2048),
    fileName: z.string().min(1).max(255).optional(),
    contentType: z.string().min(1).max(255).optional(),
    notes: z.string().max(4000).optional(),
    visibleInPortal: z.boolean().optional(),
    requiresAcknowledgement: z.boolean().optional(),
  })
  .strict();
export type SiteFileCreateRequest = z.infer<typeof SiteFileCreateRequest>;

/** Metadata both PATCH branches accept. Only the keys present are written. */
const SiteFileEditableFields = {
  title: z.string().trim().min(1).max(200).optional(),
  category: SiteFileCategorySchema.optional(),
  notes: z.string().max(4000).nullable().optional(),
  visibleInPortal: z.boolean().optional(),
  expectedUpdatedAt: z.string().datetime().optional(),
} as const;

/**
 * Metadata-only edit. The replacement-only fields are pinned ABSENT here, not
 * merely omitted: `fileName`, `contentType` and `reRequestAcknowledgement`
 * describe the NEW object and are only read on the supersede path, so a
 * payload carrying one without a replacement used to parse, take the edit
 * branch, and return 200 having silently dropped it — and the mobile outbox
 * then permanently discards an operation the server accepted but never applied
 * (review finding, round 5). `z.undefined().optional()` is the shape this file
 * already uses for the review-target union; the trailing `.optional()` is
 * load-bearing under Zod v4, which otherwise requires the key to be PRESENT.
 */
const SiteFileMetadataEdit = z
  .object({
    ...SiteFileEditableFields,
    replacementFileKey: z.undefined().optional(),
    replacementFileUrl: z.undefined().optional(),
    fileName: z.undefined().optional(),
    contentType: z.undefined().optional(),
    reRequestAcknowledgement: z.undefined().optional(),
  })
  .strict();

/**
 * REPLACE (supersede). The key and the URL are BOTH required — they name one
 * object, and a half-supplied pair was previously a route-level 400 that the
 * contract could not see. A new ACTIVE row is created, the current row flips
 * to SUPERSEDED, pending acknowledgements are retired and — when
 * acknowledgement is (re-)requested — fresh review assignments + portal
 * notifications are raised, all in one transaction.
 */
const SiteFileReplacement = z
  .object({
    ...SiteFileEditableFields,
    replacementFileKey: z.string().min(1).max(1024),
    replacementFileUrl: z.string().min(1).max(2048),
    fileName: z.string().min(1).max(255).optional(),
    contentType: z.string().min(1).max(255).nullable().optional(),
    reRequestAcknowledgement: z.boolean().optional(),
  })
  .strict();

/**
 * Edit, or REPLACE (supersede) — two SHAPES, not one object with optional
 * halves, for the same reason {@link SiteDocumentReviewRequest} is a union: an
 * accepted-but-ignored field is indistinguishable from an applied one to a
 * client that only sees the 200.
 *
 * Only the keys PRESENT are applied on the edit branch (a partial re-submit
 * must not null what it omitted). An explicit `null` on `notes` clears it.
 */
export const SiteFileUpdateRequest = z.union([
  SiteFileReplacement,
  SiteFileMetadataEdit,
]);
export type SiteFileUpdateRequest = z.infer<typeof SiteFileUpdateRequest>;

export const SiteFileReplaceResponse = z
  .object({
    file: SiteFileSchema,
    supersededFile: SiteFileSchema,
    createdAssignments: z.number().int().min(0),
    requiresReNotification: z.boolean(),
  })
  .strict();
export type SiteFileReplaceResponse = z.infer<typeof SiteFileReplaceResponse>;

/** Soft delete — the row survives, it just leaves the contractor portal. */
export const SiteFileDeleteResponse = z
  .object({ file: SiteFileSchema, hidden: z.literal(true) })
  .strict();
export type SiteFileDeleteResponse = z.infer<typeof SiteFileDeleteResponse>;

/**
 * Presign-on-read result. `url` is SHORT-LIVED and bearer-shaped (anyone
 * holding it can fetch the object until it expires) — never log it, never
 * persist it.
 */
export const SiteFileDownloadResponse = z
  .object({
    url: z.string(),
    fileName: z.string().nullable(),
    contentType: z.string().nullable(),
  })
  .strict();
export type SiteFileDownloadResponse = z.infer<typeof SiteFileDownloadResponse>;

/** Omit `portalUserIds` to notify every active portal user on the project. */
export const SiteFileNotifyRequest = z
  .object({
    portalUserIds: z.array(entityIdSchema).min(1).max(500).optional(),
  })
  .strict();
export type SiteFileNotifyRequest = z.infer<typeof SiteFileNotifyRequest>;

/**
 * `url` embeds the recipient's PORTAL MAGIC TOKEN — it is a credential, the
 * same one the web "copy link" control hands the manager. Render it, let the
 * user share it, and keep it out of logs and analytics.
 */
export const SiteFileNotifyLinkSchema = z
  .object({
    portalUserId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    url: z.string(),
  })
  .strict();
export type SiteFileNotifyLink = z.infer<typeof SiteFileNotifyLinkSchema>;

export const SiteFileNotifyResponse = z
  .object({
    attempted: z.number().int().min(0),
    links: z.array(SiteFileNotifyLinkSchema),
  })
  .strict();
export type SiteFileNotifyResponse = z.infer<typeof SiteFileNotifyResponse>;
/** Active project recipient shown in native notify/update pickers. */
export const SiteFileRecipientSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  role: z.enum(["WORKER", "CONTRACTOR_MANAGER"]),
  companyName: z.string().nullable(),
});
export type SiteFileRecipient = z.infer<typeof SiteFileRecipientSchema>;

/** Client parser: tolerate additive server fields after the app ships. */
export const SiteFileRecipientsResponse = z.object({
  recipients: z.array(SiteFileRecipientSchema),
});
export type SiteFileRecipientsResponse = z.infer<typeof SiteFileRecipientsResponse>;

/** Server boundary: reject accidental over-serialization at every level. */
export const SiteFileRecipientSchemaStrict = SiteFileRecipientSchema.strict();
export const SiteFileRecipientsResponseStrict = z
  .object({ recipients: z.array(SiteFileRecipientSchemaStrict) })
  .strict();

export const SITE_UPDATE_TARGETS = [
  "all_contractors",
  "contractor_managers",
  "selected",
] as const;
export const SiteUpdateTargetSchema = z.enum(SITE_UPDATE_TARGETS);
export type SiteUpdateTarget = (typeof SITE_UPDATE_TARGETS)[number];

export const SiteUpdateBroadcastRequest = z
  .object({
    title: z.string().trim().min(1).max(120),
    body: z.string().trim().min(1).max(1000),
    target: SiteUpdateTargetSchema,
    portalUserIds: z.array(entityIdSchema).optional(),
  })
  .strict();
export type SiteUpdateBroadcastRequest = z.infer<
  typeof SiteUpdateBroadcastRequest
>;

export const SiteUpdateRecipientSchema = z
  .object({ portalUserId: z.string(), name: z.string() })
  .strict();

export const SiteUpdateBroadcastResponse = z
  .object({
    sent: z.number().int().min(0),
    recipients: z.array(SiteUpdateRecipientSchema),
  })
  .strict();
export type SiteUpdateBroadcastResponse = z.infer<
  typeof SiteUpdateBroadcastResponse
>;

/**
 * Ask managers to acknowledge a site file (or, on Site Control, a specific
 * document version) before work continues. Web parity with
 * `app/api/projects/[projectId]/document-review-assignments`.
 *
 * A discriminated union, not one object with a loose `.refine()`: the old
 * predicate (`siteFileId` present OR the document pair present) let a payload
 * carry BOTH a `siteFileId` AND `documentId`/`documentVersionId` at once, or a
 * lone `documentVersionId` alongside a `siteFileId` with no owning
 * `documentId`. On Site Control such a mixed row can supersede a document's
 * existing reviews while the read side renders it as a site-file review
 * (`siteFile` takes precedence in the serializer), and after a downgrade to
 * Business it can vanish from the review list entirely, purely because
 * `documentVersionId` is populated — a row nothing can reach. Each branch is
 * `.strict()` with the OTHER branch's fields pinned to `z.undefined().optional()`
 * (rather than simply omitted), so a payload naming both targets fails with a
 * pointed "expected undefined" issue on the field that should not be there,
 * not a generic unrecognised-key error. The trailing `.optional()` matters on
 * its own: Zod v4 treats a bare (non-optional) key as required to be PRESENT
 * in the input object, even when its own type is `z.undefined()` — so a
 * payload that simply OMITS `documentId` (the normal, common case) would
 * otherwise fail with "expected nonoptional, received undefined" before ever
 * reaching the type check this field exists to make.
 */
const SiteDocumentReviewCommonFields = {
  assigneeUserIds: z.array(entityIdSchema).max(500).optional(),
  assigneePortalUserIds: z.array(entityIdSchema).max(500).optional(),
  target: z.enum(["all_managers"]).optional(),
  note: z.string().max(2000).nullable().optional(),
} as const;

const SiteFileReviewTarget = z
  .object({
    siteFileId: entityIdSchema,
    documentId: z.undefined().optional(),
    documentVersionId: z.undefined().optional(),
    ...SiteDocumentReviewCommonFields,
  })
  .strict();

const DocumentVersionReviewTarget = z
  .object({
    siteFileId: z.undefined().optional(),
    documentId: entityIdSchema,
    documentVersionId: entityIdSchema,
    ...SiteDocumentReviewCommonFields,
  })
  .strict();

export const SiteDocumentReviewRequest = z.union([
  SiteFileReviewTarget,
  DocumentVersionReviewTarget,
]);
export type SiteDocumentReviewRequest = z.infer<
  typeof SiteDocumentReviewRequest
>;

export const SiteDocumentReviewResponse = z
  .object({ created: z.number().int().min(0) })
  .strict();
export type SiteDocumentReviewResponse = z.infer<
  typeof SiteDocumentReviewResponse
>;
